import { Switch, Route, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { APIProvider } from "@vis.gl/react-google-maps";
import { Toaster } from "@/components/ui/toaster";
import { Loader2 } from "lucide-react";
import NotFound from "@/pages/not-found";
import AuthPage from "@/pages/auth-page";
import Dashboard from "@/pages/dashboard";
import { ProtectedRoute, FlaggedRoute } from "./lib/protected-route";
import { AuthProvider } from "./hooks/use-auth";
import ImpersonationBanner from "@/components/layout/impersonation-banner";
import * as Sentry from "@sentry/react";
import { lazy, Suspense, useEffect } from "react";

const ForgotPasswordPage = lazy(() => import("@/pages/forgot-password"));
const ResetPasswordPage = lazy(() => import("@/pages/reset-password"));
const SignupPage = lazy(() => import("@/pages/signup"));
const Clients = lazy(() => import("@/pages/clients"));
const ClientDetails = lazy(() => import("@/pages/client-details"));
const ClientUniverse = lazy(() => import("@/pages/client-universe"));
const ClientDucks = lazy(() => import("@/pages/client-ducks"));
const Ducks = lazy(() => import("@/pages/ducks"));
const Tasks = lazy(() => import("@/pages/tasks"));
const TaskBoard = lazy(() => import("@/pages/task-board"));
const Payments = lazy(() => import("@/pages/payments"));
const OvertimeRequests = lazy(() => import("@/pages/overtime-requests"));
const Missions = lazy(() => import("@/pages/missions"));
const MissionEditor = lazy(() => import("@/pages/mission-editor"));
const LegalDocuments = lazy(() => import("@/pages/legal-documents"));
const ReferralLeaderboard = lazy(() => import("@/pages/referral-leaderboard"));
const Community = lazy(() => import("@/pages/community"));
const ExemplarProfile = lazy(() => import("@/pages/exemplar-profile"));
const Settings = lazy(() => import("@/pages/settings"));
const SelectSidekick = lazy(() => import("@/pages/select-sidekick"));

function RouteFallback() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <Loader2 className="h-8 w-8 animate-spin text-border" />
    </div>
  );
}

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined;

function Router() {
  const [location] = useLocation();

  // Reset scroll to top on route change (mobile chrome stays put; content starts fresh)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  return (
    <Suspense fallback={<RouteFallback />}>
      <Switch>
        <Route path="/auth" component={AuthPage} />
        <Route path="/select-sidekick" component={SelectSidekick} />
        <Route path="/forgot-password" component={ForgotPasswordPage} />
        <Route path="/reset-password" component={ResetPasswordPage} />
        <Route path="/signup" component={SignupPage} />
        <ProtectedRoute path="/" component={Dashboard} />
        <ProtectedRoute path="/clients" component={Clients} />
        {/* Before /clients/:id so "universe" is never read as a client id.
            Keyed on client_uid, not that route's index-derived :id — clients.tsx
            synthesises it from an array position, so it changes on every
            refetch and cannot be linked to. */}
        <ProtectedRoute path="/clients/universe/:clientUid" component={ClientUniverse} />
        <ProtectedRoute
          path="/clients/universe/:clientUid/:kind/:docId"
          component={ClientUniverse}
        />
        <ProtectedRoute path="/clients/ducks/:clientUid" component={ClientDucks} />
        <ProtectedRoute path="/clients/:id" component={ClientDetails} />
        <ProtectedRoute path="/ducks" component={Ducks} />
        <FlaggedRoute path="/tasks" component={Tasks} enabled={(u) => !!u.tasks_enabled} />
        <FlaggedRoute path="/tasks/:projectId" component={TaskBoard} enabled={(u) => !!u.tasks_enabled} />
        <ProtectedRoute path="/payments" component={Payments} />
        <ProtectedRoute path="/overtime-requests" component={OvertimeRequests} />
        <ProtectedRoute path="/missions" component={Missions} />
        <ProtectedRoute path="/missions/:id" component={MissionEditor} />
        <ProtectedRoute path="/legal-documents" component={LegalDocuments} />
        <ProtectedRoute path="/referral-leaderboard" component={ReferralLeaderboard} />
        <ProtectedRoute path="/community" component={Community} />
        <ProtectedRoute path="/exemplar" component={ExemplarProfile} />
        <ProtectedRoute path="/settings" component={Settings} />
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function App() {
  useEffect(() => {
    // Test error to verify Sentry is working
    console.log("Attempting to send test error to Sentry...");
    try {
      const testError = new Error("This is a test error to verify Sentry integration");
      console.log("Test error created:", testError);
      Sentry.captureException(testError);
      console.log("Error sent to Sentry");
    } catch (error) {
      console.error("Failed to send error to Sentry:", error);
    }
  }, []);

  const tree = (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <div className="min-h-screen p-0 lg:p-4 bg-gradient-to-br from-slate-200 via-slate-100 to-gray-200 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
          <div className="min-h-screen overflow-hidden border-0 lg:min-h-[calc(100vh-2rem)] lg:rounded-2xl lg:shadow-2xl lg:border lg:border-slate-200 dark:lg:border-slate-700/60">
            <ImpersonationBanner />
            <Router />
            <Toaster />
          </div>
        </div>
      </AuthProvider>
    </QueryClientProvider>
  );

  // Pin to the "quarterly" stable channel rather than the default "weekly".
  // Google's gradual weekly rollout of Maps JS API v3.65 (~May 2026) broke
  // AdvancedMarker's `set position` setter, crashing any map for users already
  // bumped to 3.65. Quarterly trails weekly and bakes out such regressions
  // before promotion — do not remove this pin without testing maps.
  return GOOGLE_MAPS_API_KEY ? (
    <APIProvider apiKey={GOOGLE_MAPS_API_KEY} version="quarterly">
      {tree}
    </APIProvider>
  ) : (
    tree
  );
}

export default App;
